import { useForm } from "@inertiajs/react";
import { useEffect } from "react";
import {
  Badge,
  Button,
  Flex,
  Grid,
  Link,
  Select,
  Separator,
  Text,
  TextArea,
} from "@radix-ui/themes";
import { ArrowRight, Camera, ExternalLink, Instagram, Youtube } from "lucide-react";
import { badgeColors } from "@/shared/constants/constants";
import { TrackManagerApplication } from "@/types/shared";

interface Props {
  application: TrackManagerApplication;
  /** Rendered in a mobile card that already shows the applicant's name. */
  compact?: boolean;
}

export default function ApplicationReviewForm({ application, compact = false }: Props) {
  const { data, setData, patch, processing } = useForm({
    status: application.status,
    rejection_reason: application.rejection_reason ?? "",
    reviewer_notes: application.reviewer_notes ?? "",
  });

  // Re-initialize form data when navigating to a different application
  useEffect(() => {
    setData({
      status: application.status,
      rejection_reason: application.rejection_reason ?? "",
      reviewer_notes: application.reviewer_notes ?? "",
    });
  }, [application.id]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    patch(application.update_path, {
      preserveState: true,
      preserveScroll: true,
    });
  }

  const { participant } = application;

  return (
    <form onSubmit={handleSubmit}>
      {/* Applicant info. Only the name is duplicated by the card — the email, the
          socials and the profile link are exactly what you review them on, so
          `compact` drops the name and keeps the rest. */}
      <Flex direction="column" gap="1" mb="3">
        {!compact && (
          <Flex align="center" gap="2">
            <Text size="3" weight="bold">
              {participant.name}
            </Text>
            {participant.participantable_type === "MediaGroup" && (
              <Link href={`/media_groups/${participant.participantable_id}`} target="_blank" color="gray">
                <ExternalLink size={14} />
              </Link>
            )}
          </Flex>
        )}
        <Flex align="center" gap="2">
          <Text size="1" color="gray">
            {participant.participantable_type}
          </Text>
          {/* The profile link lives on the name row on desktop; without that row
              it would be lost, so it comes along here instead. */}
          {compact && participant.participantable_type === "MediaGroup" && (
            <Link href={`/media_groups/${participant.participantable_id}`} target="_blank" color="gray">
              <ExternalLink size={14} />
            </Link>
          )}
        </Flex>
        {participant.email && (
          <Text size="1" color="gray">
            {participant.email}
          </Text>
        )}
        <Flex gap="3" mt="1">
          {participant.instagram && (
            <Link
              href={`https://instagram.com/${participant.instagram}`}
              target="_blank"
              size="1"
            >
              <Flex align="center" gap="1">
                <Instagram size={14} />
                {participant.instagram}
              </Flex>
            </Link>
          )}
          {participant.youtube && (
            <Link href={participant.youtube} target="_blank" size="1">
              <Flex align="center" gap="1">
                <Youtube size={14} />
                YouTube
              </Flex>
            </Link>
          )}
          {participant.flickr && (
            <Link href={participant.flickr} target="_blank" size="1">
              <Flex align="center" gap="1">
                <Camera size={14} />
                Flickr
              </Flex>
            </Link>
          )}
        </Flex>
      </Flex>

      <Separator size="4" mb="3" />

      {/* Event info */}
      <Flex direction="column" gap="1" mb="4">
        <Flex gap="2">
          <Text size="2" color="gray" style={{ minWidth: 56 }}>
            Event
          </Text>
          <Text size="2">{application.event_name}</Text>
        </Flex>
        <Flex gap="2">
          <Text size="2" color="gray" style={{ minWidth: 56 }}>
            Location
          </Text>
          <Text size="2">{application.event_location}</Text>
        </Flex>
        <Flex gap="2">
          <Text size="2" color="gray" style={{ minWidth: 56 }}>
            Date
          </Text>
          <Text size="2">{application.event_date}</Text>
        </Flex>
      </Flex>

      {/* Annual credential — review it before deciding the application */}
      {application.credential && (
        <>
          <Separator size="4" mb="3" />
          <Flex direction="column" gap="2" mb="4">
            <Text size="2" weight="medium">
              Annual Credential
            </Text>
            <Flex align="center" gap="2">
              <Badge variant="soft" color="gray" style={{ textTransform: "capitalize" }}>
                {application.credential.category}
              </Badge>
              <Badge
                variant="soft"
                color={badgeColors[application.credential.status]}
                style={{ textTransform: "capitalize" }}
              >
                {application.credential.status}
              </Badge>
              {application.credential.expired && (
                <Badge variant="soft" color="crimson">
                  Expired
                </Badge>
              )}
            </Flex>
            {application.credential.status === "pending" && (
              <Text size="1" color="gray">
                Review the credential first — approving it here won't approve the application.
              </Text>
            )}
            {/* Absent where the section has no credential queue to send them to. */}
            {application.credential.review_path && (
              <Link href={application.credential.review_path} size="2">
                <Flex align="center" gap="1">
                  Open credential
                  <ArrowRight size={12} />
                </Flex>
              </Link>
            )}
          </Flex>
        </>
      )}

      {/* Attended events (Media and Staff only) */}
      {["MediaGroup", "Staff"].includes(participant.participantable_type) && participant.attended_events.length > 0 && (
        <>
          <Separator size="4" mb="3" />
          <Flex direction="column" gap="2" mb="3">
            <Text size="2" weight="medium">
              Previously Attended
            </Text>
            <Flex direction="column" gap="2" p="2">
              {participant.attended_events.map((e) => (
                <Link key={e.show_path} href={e.show_path} target="_blank" size="2">
                  <Flex direction="column" gap="0">
                    <Flex align="center" gap="1">
                      {e.name}
                      <ArrowRight size={12} />
                    </Flex>
                    <Text size="1" color="gray">{e.date}</Text>
                  </Flex>
                </Link>
              ))}

            </Flex>
            
          </Flex>
        </>
      )}

      <Separator size="4" mb="3" />

      {/* Rejection reason */}
      <Flex direction="column" gap="1" mb="3">
        <Text size="2" as="label" weight="medium">
          Rejection Reason
        </Text>
        <TextArea
          value={data.rejection_reason}
          onChange={(e) => setData("rejection_reason", e.target.value)}
          placeholder="Optional — visible to applicant"
          rows={3}
        />
      </Flex>

      {/* Reviewer notes */}
      <Flex direction="column" gap="1" mb="3">
        <Text size="2" as="label" weight="medium">
          Reviewer Notes
        </Text>
        <TextArea
          value={data.reviewer_notes}
          onChange={(e) => setData("reviewer_notes", e.target.value)}
          placeholder="Internal notes"
          rows={3}
        />
      </Flex>

      {/* Status */}
      <Flex direction="column" gap="1" mb="4">
        <Text size="2" as="label" weight="medium">
          Status
        </Text>
        <Select.Root
          value={data.status}
          onValueChange={(v) => setData("status", v)}
        >
          <Select.Trigger />
          <Select.Content>
            <Select.Item value="pending">Pending</Select.Item>
            <Select.Item value="approved">Approved</Select.Item>
            <Select.Item value="waitlisted">Waitlisted</Select.Item>
            <Select.Item value="rejected">Rejected</Select.Item>
          </Select.Content>
        </Select.Root>
      </Flex>

      <Button type="submit" loading={processing} style={{ width: "100%" }}>
        Save
      </Button>
    </form>
  );
}
